import {useEffect, useMemo, useRef, useState} from "react";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {elevation} from "@pg-design/elevation";
import {Container} from "@pg-design/grid-module";
import {backgroundColor, borderRadius, calculateRemSize, flex, flexDirection, ml, pb, ph, position, textAlign, w100} from "@pg-design/helpers-css";
import {ChevronDownIcon, ChevronUpIcon, IIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {useUserDevice} from "@pg-mono/user-device";

import {useClickOutside} from "../../../../hooks/handle_click_outside";
import {createOfferLink, OFFER_DETAIL_ALLOW_SHOW_SOLD_STAGE_PARAM} from "../../../helpers/create_offer_link";
import {getOfferStageText} from "../../../helpers/get_offer_stage_text";
import {IOfferGroups, StageStatus} from "../../types/IOfferGroups";
import {OfferDetailStage, StageWrapper} from "./OfferDetailsStage";
import {OfferStageStatus} from "./OfferStageStatus";

const iconProps: IIcon = {
    wrapperColor: "transparent",
    size: "2.4",
    wrapperSize: "2.4"
};

interface IProps {
    offer: {
        groups: IOfferGroups | null;
        id: number;
    };
    isArchived?: boolean;
}

export const OfferDetailStages = (props: IProps) => {
    const {isMobile} = useUserDevice();
    const theme = useTheme();

    const dropdownRef = useRef(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const [moreStagesOpen, setMoreStagesOpen] = useState(false);

    const {hasMoreButton, moreStages, stages, activeStageIndex} = useMemo(() => {
        const stagesSortedBySold = [...(props.offer.groups?.stages || [])]
            .filter((stage) => !stage.hidden)
            .sort((a, b) => (a.status === StageStatus.SOLD ? 1 : b.status === StageStatus.SOLD ? -1 : 0));
        const activeStageIndex = stagesSortedBySold.findIndex((stage) => stage.offer.id === props.offer.id);

        if (isMobile) {
            return {
                hasMoreButton: false,
                moreStages: [],
                stages: stagesSortedBySold,
                activeStageIndex
            };
        }

        const visibleStagesMaxCount = 5;
        const visibleSoldStagesMaxCount = 2;
        const hasHiddenStages = stagesSortedBySold && stagesSortedBySold.length > visibleStagesMaxCount;
        const soldStages = stagesSortedBySold.filter((item) => item.status === StageStatus.SOLD);
        const hasHiddenSoldStages = soldStages.length > visibleSoldStagesMaxCount;
        const stagesFilteredBySold = hasHiddenSoldStages ? stagesSortedBySold.filter((item) => item.status !== StageStatus.SOLD) : stagesSortedBySold;
        const mainStages = stagesFilteredBySold.slice(0, visibleStagesMaxCount);

        const moreStages = [];
        if (hasHiddenSoldStages) {
            moreStages.push(...soldStages);
        }
        if (hasHiddenStages) {
            moreStages.push(...stagesFilteredBySold.slice(visibleStagesMaxCount));
        }
        return {
            stages: mainStages,
            moreStages,
            hasMoreButton: hasHiddenStages || hasHiddenSoldStages
        };
    }, [props.offer.groups, isMobile]);

    useClickOutside(dropdownRef, () => setMoreStagesOpen(false));

    useEffect(() => {
        if (isMobile && activeStageIndex && wrapperRef.current) {
            const defaultStageSize = 160;
            const gapSize = 8;
            wrapperRef.current.scrollLeft += activeStageIndex * (defaultStageSize + gapSize);
        }
    }, []);

    const renderMoreStages = () =>
        moreStages.map((stage) => {
            const link = createOfferLink(
                {...stage.offer, groups: props.offer.groups},
                stage.status === StageStatus.SOLD ? {[OFFER_DETAIL_ALLOW_SHOW_SOLD_STAGE_PARAM]: true} : {}
            );
            const isActive = stage.offer.id === props.offer.id;

            return (
                <AdditionalStageWrapper href={link} target="_blank" key={stage.id} isActive={isActive}>
                    <div>
                        <Text variant="body_copy_1">{stage.name}</Text>
                        <Text variant="info_txt_1" color={theme.colors.gray[700]}>
                            {getOfferStageText(stage.status)}
                        </Text>
                    </div>
                    <OfferStageStatus status={stage.status} />
                </AdditionalStageWrapper>
            );
        });

    return !!stages.length ? (
        <Container css={stagesStyle} ref={wrapperRef}>
            <Wrapper stagesCount={stages.length}>
                {stages.map((stage) => (
                    <OfferDetailStage stage={stage} groups={props.offer.groups} key={stage.id} />
                ))}
                {hasMoreButton && (
                    <StageWrapper as="div" css={moreStagesStyle} onClick={() => setMoreStagesOpen((prev) => !prev)} ref={dropdownRef} disabled={false}>
                        <Text variant="button_medium" css={textAlign("center")}>
                            WIĘCEJ ETAPÓW
                        </Text>
                        {moreStagesOpen ? <ChevronUpIcon {...iconProps} /> : <ChevronDownIcon {...iconProps} />}
                        {moreStagesOpen && !isMobile && <div css={dropdown}>{renderMoreStages()}</div>}
                    </StageWrapper>
                )}
                <div css={spacing} />
            </Wrapper>
        </Container>
    ) : null;
};

interface IWrapperProps {
    stagesCount: number;
}

const Wrapper = styled.div<IWrapperProps>`
    ${flex("center")};
    gap: ${calculateRemSize(1)};
    ${w100};
    ${pb(2)};

    @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
        ${ph(0)};
        ${ml(0)};
    }

    ${(props) =>
        props.stagesCount >= 5 &&
        css`
            @media (min-width: ${props.theme.breakpoints.md}) {
                > * {
                    width: 100%;
                }
            }
        `}

    ${(props) =>
        props.stagesCount === 2 &&
        css`
            > a {
                flex-grow: 1;
            }
            > div {
                display: none;
            }
            @media (min-width: ${props.theme.breakpoints.sm}) {
                > a {
                    flex-grow: initial;
                }
            }
            @media (min-width: ${props.theme.breakpoints.md}) {
                > div {
                    display: flex;
                }
            }
        `}
`;

const moreStagesStyle = (theme: Theme) => css`
    height: ${calculateRemSize(7)};
    flex-grow: 1;
    justify-content: center;
    position: relative;
    max-width: 296px;
    min-width: 296px;

    span {
        margin: 0 auto;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        min-width: initial;
    }
`;

const dropdown = css`
    ${flex("center")};
    ${flexDirection("column")};
    ${position("absolute")};
    ${borderRadius(2)};
    ${backgroundColor("#fff")};
    bottom: -${calculateRemSize(1)};
    transform: translateY(100%);
    ${elevation(4)};
    ${ph(2)};
    z-index: 1;
    width: 100%;
    max-height: 300px;
    overflow-y: auto;
    overflow-x: hidden;
`;

interface IAdditionalStageWrapperProps {
    isActive: boolean;
}

const AdditionalStageWrapper = styled.a<IAdditionalStageWrapperProps>`
    ${flex("center", "space-between")};
    ${ph(1)};
    min-height: ${calculateRemSize(8)};
    border: none;
    max-width: 100%;
    width: 100%;

    p:first-of-type {
        color: ${(props) => props.theme.colors.secondary};
    }

    &:not(:last-of-type) {
        border-bottom: 1px solid ${(props) => props.theme.colors.gray[200]};
    }

    ${(props) =>
        props.isActive &&
        css`
            color: red;
            background-color: ${props.theme.colors.background[200]};
            width: calc(100% + ${calculateRemSize(4)});
            max-width: initial;
            ${ph(3)};
        `}
`;

const spacing = (theme: Theme) => css`
    min-width: ${calculateRemSize(0.5)};
    min-height: 1px;

    @media (min-width: ${theme.breakpoints.sm}) {
        min-width: calc(0.5 * (100vw - ${theme.breakpoints.sm}) + ${calculateRemSize(0.5)});
    }

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const stagesStyle = (theme: Theme) => css`
    width: 100vw;
    max-width: 100vw;
    ${ph(1.5)};
    margin-left: initial;
    margin-right: initial;
    overflow-x: auto;

    @media (min-width: ${theme.breakpoints.sm}) {
        margin-left: auto;
        max-width: 100vw;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        width: 100%;
        margin-right: auto;
        max-width: 1192px;
        overflow-x: visible;
    }

    @media (min-width: ${theme.breakpoints.lg}) {
        max-width: 1576px;
    }
`;
import {useEffect, useMemo, useRef, useState} from "react";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {elevation} from "@pg-design/elevation";
import {Container} from "@pg-design/grid-module";
import {backgroundColor, borderRadius, calculateRemSize, flex, flexDirection, ml, pb, ph, position, textAlign, w100} from "@pg-design/helpers-css";
import {ChevronDownIcon, ChevronUpIcon, IIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {useUserDevice} from "@pg-mono/user-device";

import {useClickOutside} from "../../../../hooks/handle_click_outside";
import {createOfferLink, OFFER_DETAIL_ALLOW_SHOW_SOLD_STAGE_PARAM} from "../../../helpers/create_offer_link";
import {getOfferStageText} from "../../../helpers/get_offer_stage_text";
import {IOfferGroups, StageStatus} from "../../types/IOfferGroups";
import {OfferDetailStage, StageWrapper} from "./OfferDetailsStage";
import {OfferStageStatus} from "./OfferStageStatus";

const iconProps: IIcon = {
    wrapperColor: "transparent",
    size: "2.4",
    wrapperSize: "2.4"
};

interface IProps {
    offer: {
        groups: IOfferGroups | null;
        id: number;
    };
    isArchived?: boolean;
}

export const OfferDetailStages = (props: IProps) => {
    const {isMobile} = useUserDevice();
    const theme = useTheme();

    const dropdownRef = useRef(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const [moreStagesOpen, setMoreStagesOpen] = useState(false);

    const {hasMoreButton, moreStages, stages, activeStageIndex} = useMemo(() => {
        const stagesSortedBySold = [...(props.offer.groups?.stages || [])]
            .filter((stage) => !stage.hidden)
            .sort((a, b) => (a.status === StageStatus.SOLD ? 1 : b.status === StageStatus.SOLD ? -1 : 0));
        const activeStageIndex = stagesSortedBySold.findIndex((stage) => stage.offer.id === props.offer.id);

        if (isMobile) {
            return {
                hasMoreButton: false,
                moreStages: [],
                stages: stagesSortedBySold,
                activeStageIndex
            };
        }

        const visibleStagesMaxCount = 5;
        const visibleSoldStagesMaxCount = 2;
        const hasHiddenStages = stagesSortedBySold && stagesSortedBySold.length > visibleStagesMaxCount;
        const soldStages = stagesSortedBySold.filter((item) => item.status === StageStatus.SOLD);
        const hasHiddenSoldStages = soldStages.length > visibleSoldStagesMaxCount;
        const stagesFilteredBySold = hasHiddenSoldStages ? stagesSortedBySold.filter((item) => item.status !== StageStatus.SOLD) : stagesSortedBySold;
        const mainStages = stagesFilteredBySold.slice(0, visibleStagesMaxCount);

        const moreStages = [];
        if (hasHiddenSoldStages) {
            moreStages.push(...soldStages);
        }
        if (hasHiddenStages) {
            moreStages.push(...stagesFilteredBySold.slice(visibleStagesMaxCount));
        }
        return {
            stages: mainStages,
            moreStages,
            hasMoreButton: hasHiddenStages || hasHiddenSoldStages
        };
    }, [props.offer.groups, isMobile]);

    useClickOutside(dropdownRef, () => setMoreStagesOpen(false));

    useEffect(() => {
        if (isMobile && activeStageIndex && wrapperRef.current) {
            const defaultStageSize = 160;
            const gapSize = 8;
            wrapperRef.current.scrollLeft += activeStageIndex * (defaultStageSize + gapSize);
        }
    }, []);

    const renderMoreStages = () =>
        moreStages.map((stage) => {
            const link = createOfferLink(
                {...stage.offer, groups: props.offer.groups},
                stage.status === StageStatus.SOLD ? {[OFFER_DETAIL_ALLOW_SHOW_SOLD_STAGE_PARAM]: true} : {}
            );
            const isActive = stage.offer.id === props.offer.id;

            return (
                <AdditionalStageWrapper href={link} target="_blank" key={stage.id} isActive={isActive}>
                    <div>
                        <Text variant="body_copy_1">{stage.name}</Text>
                        <Text variant="info_txt_1" color={theme.colors.gray[700]}>
                            {getOfferStageText(stage.status)}
                        </Text>
                    </div>
                    <OfferStageStatus status={stage.status} />
                </AdditionalStageWrapper>
            );
        });

    return !!stages.length ? (
        <Container css={stagesStyle} ref={wrapperRef}>
            <Wrapper stagesCount={stages.length}>
                {stages.map((stage) => (
                    <OfferDetailStage stage={stage} groups={props.offer.groups} key={stage.id} />
                ))}
                {hasMoreButton && (
                    <StageWrapper as="div" css={moreStagesStyle} onClick={() => setMoreStagesOpen((prev) => !prev)} ref={dropdownRef} disabled={false}>
                        <Text variant="button_medium" css={textAlign("center")}>
                            WIĘCEJ ETAPÓW
                        </Text>
                        {moreStagesOpen ? <ChevronUpIcon {...iconProps} /> : <ChevronDownIcon {...iconProps} />}
                        {moreStagesOpen && !isMobile && <div css={dropdown}>{renderMoreStages()}</div>}
                    </StageWrapper>
                )}
                <div css={spacing} />
            </Wrapper>
        </Container>
    ) : null;
};

interface IWrapperProps {
    stagesCount: number;
}

const Wrapper = styled.div<IWrapperProps>`
    ${flex("center")};
    gap: ${calculateRemSize(1)};
    ${w100};
    ${pb(2)};

    @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
        ${ph(0)};
        ${ml(0)};
    }

    ${(props) =>
        props.stagesCount >= 5 &&
        css`
            @media (min-width: ${props.theme.breakpoints.md}) {
                > * {
                    width: 100%;
                }
            }
        `}

    ${(props) =>
        props.stagesCount === 2 &&
        css`
            > a {
                flex-grow: 1;
            }
            > div {
                display: none;
            }
            @media (min-width: ${props.theme.breakpoints.sm}) {
                > a {
                    flex-grow: initial;
                }
            }
            @media (min-width: ${props.theme.breakpoints.md}) {
                > div {
                    display: flex;
                }
            }
        `}
`;

const moreStagesStyle = (theme: Theme) => css`
    height: ${calculateRemSize(7)};
    flex-grow: 1;
    justify-content: center;
    position: relative;
    max-width: 296px;
    min-width: 296px;

    span {
        margin: 0 auto;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        min-width: initial;
    }
`;

const dropdown = css`
    ${flex("center")};
    ${flexDirection("column")};
    ${position("absolute")};
    ${borderRadius(2)};
    ${backgroundColor("#fff")};
    bottom: -${calculateRemSize(1)};
    transform: translateY(100%);
    ${elevation(4)};
    ${ph(2)};
    z-index: 1;
    width: 100%;
    max-height: 300px;
    overflow-y: auto;
    overflow-x: hidden;
`;

interface IAdditionalStageWrapperProps {
    isActive: boolean;
}

const AdditionalStageWrapper = styled.a<IAdditionalStageWrapperProps>`
    ${flex("center", "space-between")};
    ${ph(1)};
    min-height: ${calculateRemSize(8)};
    border: none;
    max-width: 100%;
    width: 100%;

    p:first-of-type {
        color: ${(props) => props.theme.colors.secondary};
    }

    &:not(:last-of-type) {
        border-bottom: 1px solid ${(props) => props.theme.colors.gray[200]};
    }

    ${(props) =>
        props.isActive &&
        css`
            color: red;
            background-color: ${props.theme.colors.background[200]};
            width: calc(100% + ${calculateRemSize(4)});
            max-width: initial;
            ${ph(3)};
        `}
`;

const spacing = (theme: Theme) => css`
    min-width: ${calculateRemSize(0.5)};
    min-height: 1px;

    @media (min-width: ${theme.breakpoints.sm}) {
        min-width: calc(0.5 * (100vw - ${theme.breakpoints.sm}) + ${calculateRemSize(0.5)});
    }

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const stagesStyle = (theme: Theme) => css`
    width: 100vw;
    max-width: 100vw;
    ${ph(1.5)};
    margin-left: initial;
    margin-right: initial;
    overflow-x: auto;

    @media (min-width: ${theme.breakpoints.sm}) {
        margin-left: auto;
        max-width: 100vw;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        width: 100%;
        margin-right: auto;
        max-width: 1192px;
        overflow-x: visible;
    }

    @media (min-width: ${theme.breakpoints.lg}) {
        max-width: 1576px;
    }
`;
